<script setup lang="ts">
import RcSiteSelect, { type ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { CalendarMode, useWeeksCalendar } from '@/composables/eos/weekday-calendar'
import {
  getBaselineResetDayEstimation,
  getBaseLineResetDaysBetweenDates,
} from '@/services/eos/baseline-reset-days-tracker.service'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { useQuery } from '@tanstack/vue-query'
import { DateTime } from 'luxon'
import { computed, ref } from 'vue'

const selectedSite = ref<ISiteIdentifier>()
const selectedDate = ref<Date | null>(new Date())
const activeTab = ref<CalendarMode>(DateTime.now().isWeekend ? CalendarMode.Weekends : CalendarMode.Weekdays)

const selectedDateTime = computed<DateTime | null>({
  get: () => (selectedDate.value ? DateTime.fromJSDate(selectedDate.value) : null),
  set: (newValue) => {
    selectedDate.value = newValue ? newValue.toJSDate() : null
  },
})

// Base time window (centers around selected date or today if none selected):

// 45 days in the past, then adjust to the start of the period
const rangeStartDate = computed(() => {
  const base = selectedDateTime.value ?? DateTime.now()
  return base.minus({ days: 45 })
})

// Selected day or today if none selected
const rangeEndDate = computed(() => {
  const base = selectedDateTime.value ?? DateTime.now()
  return base
})

const maxObservedDate = computed(() => {
  const projectionDates = observedNextResetProjections.value.map((p) => p.date)
  const furthestProjection = projectionDates.reduce<DateTime | null>((max, d) => (!max || d > max ? d : max), null)
  if (!furthestProjection) return rangeEndDate.value
  return rangeEndDate.value > furthestProjection ? rangeEndDate.value : furthestProjection
})

// Server bindings

const betweenDatesQuery = useQuery({
  queryFn: () =>
    getBaseLineResetDaysBetweenDates(
      rangeStartDate.value.toISODate()!,
      rangeEndDate.value.toISODate()!,
      selectedSite.value!.site_id! // ensured to be set...
    ),
  queryKey: ['reset-days-between-dates', selectedSite, selectedDate, activeTab, rangeStartDate, rangeEndDate],
  enabled: computed(
    () =>
      !!selectedSite.value?.site_id && // ...here
      !!selectedDateTime.value?.isValid &&
      !!rangeStartDate.value?.isValid &&
      !!rangeEndDate.value?.isValid
  ),
  // Unpack dates
  select: (data) => ({
    ...data,
    start_date: DateTime.fromISO(data.start_date),
    end_date: DateTime.fromISO(data.end_date),
    baseline_reset_days: data.baseline_reset_days.map((d) => DateTime.fromISO(d)),
  }),
})

const fromDayQuery = useQuery({
  queryFn: () => getBaselineResetDayEstimation(selectedDateTime.value!.toISODate()!, selectedSite.value!.site_id!),
  queryKey: ['next-reset-date-from-date', selectedSite, selectedDate, activeTab, rangeStartDate, rangeEndDate],
  enabled: computed(() => !!selectedSite.value?.site_id && !!selectedDateTime.value?.isValid),
  select: (data) => ({
    ...data,
    selected_date: DateTime.fromISO(data.selected_date),
    last_baseline_reset_days: data.last_baseline_reset_days.map((d) => DateTime.fromISO(d)),
    next_baseline_reset_days: data.next_baseline_reset_days.map((p) => ({ ...p, date: DateTime.fromISO(p.date) })),
  }),
})

// Use calendar

const { headers, dates, valid, hasWeekdayHoliday } = useWeeksCalendar(rangeStartDate, maxObservedDate, activeTab)

// Derive UI values from the queries:

const datesIncludeAnyWeekdayHoliday = computed(() => {
  return dates.value.some((d) => !!d.holiday && !d.date.isWeekend)
})

/** Hydrates dates data with UIState/Query-related "appendices" */
const uiDates = computed(() => {
  return dates.value
    .map((d) => {
      const matchedProjection = observedNextResetProjections.value.find((p) => d.date.hasSame(p.date, 'day'))
      return {
        ...d,
        isToday: d.date.hasSame(DateTime.now(), 'day'),
        isSelected: !!selectedDateTime.value && d.date.hasSame(selectedDateTime.value, 'day'),
        isNonDispatch:
          observedLastResetDays.value && observedLastResetDays.value.some((rd) => d.date.hasSame(rd, 'day')),
        isNextReset: !!matchedProjection,
        isDirtyReset: matchedProjection?.dirty_baseline ?? false,
        isOutOfRange:
          rangeStartDate.value &&
          rangeEndDate.value &&
          (d.date < rangeStartDate.value || d.date > maxObservedDate.value),
      }
    })
    .map((d) => ({
      ...d,
      crowded: [d.isToday, d.isSelected, d.isNonDispatch, d.isNextReset, d.holiday].filter(Boolean).length > 1,
    }))
})

/** Pads dates for weekend view when holidays exist in weekdays (three columns needed instead of two) */
const paddedDates = computed(() => {
  if (!valid.value) return []

  // The only case in which we need to pad dates is when there are holidays in weekdays while we're looking at weekends.
  // In such case, we add nulls to render empty divs in the 'Holidays' column where there isn't any.
  if (activeTab.value === CalendarMode.Weekdays || !datesIncludeAnyWeekdayHoliday.value) {
    return uiDates.value
  }

  const result: ((typeof uiDates.value)[number] | null)[] = []

  // Get a weeks array
  const weeks = [...new Set(uiDates.value.map((d) => d.date.startOf('week').toISODate()))]

  weeks.forEach((weekStart) => {
    const weekDates = uiDates.value.filter((d) => d.date.startOf('week').toISODate() === weekStart)

    // Find the pieces for our 3 columns
    const holidayEntry = weekDates.find((d) => !!d.holiday && !d.date.isWeekend)
    const saturdayEntry = weekDates.find((d) => d.date.weekday === 6)
    const sundayEntry = weekDates.find((d) => d.date.weekday === 7)

    // Push exactly 3 items per week to the grid, null means 'nothing to render here'
    result.push(holidayEntry || null)
    result.push(saturdayEntry || null)
    result.push(sundayEntry || null)
  })

  return result
})

const observedLastResetDays = computed(() => {
  if (!selectedDate.value) return []

  // If a date is selected, pull from fromDayQuery instead
  if (selectedDate.value && fromDayQuery.data.value) return fromDayQuery.data.value.last_baseline_reset_days

  return []
})

const observedNextResetProjections = computed(() => {
  if (!selectedSite.value || anyLoading.value || !fromDayQuery.data.value) return []
  return fromDayQuery.data.value.next_baseline_reset_days
})

/** The nearest of the (up to 3) rolling reset-day projections */
const nextResetProjection = computed(() => observedNextResetProjections.value[0] ?? null)

const daysToNextReset = computed(() => {
  if (!selectedDate.value || !selectedSite.value || anyLoading.value) return '-'
  const compareTo = selectedDateTime.value ?? DateTime.now()
  return nextResetProjection.value ? Math.ceil(nextResetProjection.value.date.diff(compareTo, 'days').days) : '-'
})

const nonDispatchDaysInRange = computed(() => {
  if (!selectedDate.value || !selectedSite.value || anyLoading.value) return '-'
  if (!selectedDate.value) return betweenDatesQuery.data.value?.baseline_reset_days.length ?? '-'
  return fromDayQuery.data.value?.last_baseline_reset_days.length ?? '-'
})

// Date left and right chevron handlers

function setPreviousDay() {
  if (!selectedDateTime.value) return
  const previousDay = selectedDateTime.value.minus({ days: 1 })
  selectedDateTime.value = previousDay

  // Switch tab if needed:
  if (previousDay.isWeekend) {
    activeTab.value = CalendarMode.Weekends
  } else {
    activeTab.value = CalendarMode.Weekdays
  }
}

function setNextDay() {
  if (!selectedDateTime.value) return
  const nextDay = selectedDateTime.value.plus({ days: 1 })
  selectedDateTime.value = nextDay

  // Switch tab if needed:
  if (nextDay.isWeekend) {
    activeTab.value = CalendarMode.Weekends
  } else {
    activeTab.value = CalendarMode.Weekdays
  }
}

// Query state management

const anyError = computed(() => {
  return [betweenDatesQuery.error.value, fromDayQuery.error.value].filter(Boolean).length > 0
})

const anyLoading = computed(() => betweenDatesQuery.isPending.value || fromDayQuery.isPending.value)

const isRefetching = computed(() => betweenDatesQuery.isRefetching.value || fromDayQuery.isRefetching.value)

const aggregateError = computed(() => {
  if (betweenDatesQuery.isError) return betweenDatesQuery.error
  if (fromDayQuery.isError) return fromDayQuery.error
  return null
})

const refetchAll = () => {
  betweenDatesQuery.refetch()
  fromDayQuery.refetch()
}

/** Util that disables date picker weekdates/weekends according to tab selection */
const isDateDisabled = (date: Date) => {
  return activeTab.value === CalendarMode.Weekends
    ? // Disable weekdays (Mon-Fri are 1-5)
      date.getDay() >= 1 && date.getDay() <= 5
    : // Disable weekends (Sun=0, Sat=6)
      date.getDay() === 0 || date.getDay() === 6
}
</script>

<template>
  <ViewLayout>
    <el-card class="w-full self-center">
      <template #header>
        <!-- Tabs -->
        <el-tabs v-model="activeTab" class="min-w-64 max-w-64" @tab-click="selectedDate = null">
          <el-tab-pane label="Weekdays" :name="CalendarMode.Weekdays" />
          <el-tab-pane label="Weekends" :name="CalendarMode.Weekends" />
        </el-tabs>

        <!-- Site select -->
        <RcSiteSelect v-model="selectedSite" class="w-[220px]" />

        <!-- Date picker -->
        <div class="flex gap-2 items-center text-text-secondary">
          <Icon
            :class="{
              'cursor-pointer': !!selectedDate,
              'text-text-tertiary': !selectedDate,
            }"
            icon="mdi:chevron-left"
            :height="20"
            @click="setPreviousDay"
          />
          <el-date-picker v-model="selectedDate" type="date" placeholder="Pick a day" :disabled-date="isDateDisabled" />
          <Icon
            :class="{
              'cursor-pointer': !!selectedDate,
              'text-text-tertiary': !selectedDate,
              'opacity-50': selectedDateTime && selectedDateTime >= DateTime.now().startOf('day'),
            }"
            icon="mdi:chevron-right"
            :height="20"
            @click="setNextDay"
          />
        </div>
      </template>

      <div v-if="anyError && !isRefetching" class="h-114 flex items-center justify-center">
        <rl-error-result :error="aggregateError">
          <el-button @click="() => refetchAll()">
            <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
          </el-button>
        </rl-error-result>
      </div>

      <div
        v-else-if="!selectedSite || !selectedDate"
        class="h-114 flex items-center justify-center gap-2 text-text-tertiary"
      >
        <Icon icon="mdi:alert-circle-outline" height="20" />
        Please select a site and date to view the calendar.
      </div>

      <div
        v-else-if="selectedSite && selectedDate && anyLoading"
        class="h-114 flex items-center justify-center gap-2 text-text-tertiary"
      >
        <Icon icon="eos-icons:three-dots-loading" height="20" />
        Loading...
      </div>

      <div v-else-if="!valid" class="flex w-full h-128 items-center justify-center">
        <div class="flex gap-3 items-center text-text-tertiary text-3xl">
          <Icon icon="mdi:calendar" /> <span>Pick a day</span>
        </div>
      </div>

      <!-- Set headers to a standalone grid so they don't scroll along with the UI -->
      <div
        v-if="valid && !anyLoading && !anyError"
        :class="[
          'grid gap-4 mx-auto max-w-[1280px] mb-4 px-6',
          {
            'cols-5': activeTab === CalendarMode.Weekdays,
            'cols-2 max-w-[600px]': activeTab === CalendarMode.Weekends,
            'cols-3 max-w-[800px]': activeTab === CalendarMode.Weekends && hasWeekdayHoliday,
          },
        ]"
      >
        <!-- Headers -->
        <span v-for="dayShort in headers" :key="dayShort" class="text-text-tertiary">
          {{ dayShort }}
        </span>
      </div>

      <div
        v-if="valid && !anyLoading && !anyError"
        :class="[
          'grid gap-4 mx-auto max-w-[1280px] max-h-104 overflow-y-auto pb-8 px-6',
          {
            'cols-5': activeTab === CalendarMode.Weekdays,
            'cols-2 max-w-[600px]': activeTab === CalendarMode.Weekends,
            'cols-3 max-w-[800px]': activeTab === CalendarMode.Weekends && hasWeekdayHoliday,
          },
        ]"
      >
        <template v-for="(entry, index) in paddedDates">
          <!-- Padding div -->
          <div v-if="!entry" :key="index" />

          <!-- Date cards -->
          <el-card
            v-if="entry"
            :key="entry.date.toISODate()"
            :class="[
              'calendar-date',
              {
                'border-sky-600': entry.isSelected || entry.isToday,
                'border-orange-600 bg-orange-500/20': entry.isNextReset,
                'border-emerald-600 bg-emerald-500/20': entry.isNonDispatch,
                'opacity-20 cursor-not-allowed':
                  entry.isOutOfRange || (entry.holiday && activeTab !== CalendarMode.Weekends),
              },
            ]"
          >
            <div class="h-full flex items-center">
              <!-- Date text -->
              <span :class="['w-6 text-right', { 'text-text-tertiary': entry.holiday }]">{{ entry.date.day }}</span>
              <span class="pl-1 text-text-tertiary">{{ entry.date.monthShort }}</span>

              <div :class="['ml-auto flex flex-col items-end text-sm', { 'text-xs': entry.crowded }]">
                <!-- Status labels, we make them xs if also a holiday in order to fit both texts better -->
                <span v-if="entry.isToday" class="text-text-brand">Today</span>
                <span v-if="entry.isSelected" class="text-text-brand">Selected </span>
                <span v-if="entry.isNextReset" class="ml-auto flex items-center gap-1 text-orange-600">
                  Reset
                  <el-tooltip
                    v-if="entry.isDirtyReset"
                    content="Best-effort estimate — fewer than the standard number of historical reset days were available for this projection."
                    placement="top"
                  >
                    <Icon icon="mdi:alert" height="12" class="text-amber-500 cursor-help" />
                  </el-tooltip>
                </span>
                <span v-if="entry.isNonDispatch" class="ml-auto text-emerald-400">Non-dispatch</span>
                <span v-if="entry.holiday" class="ml-auto text-text-tertiary text-xs">
                  {{ entry.holiday.name }}
                </span>
              </div>
            </div>
          </el-card>
        </template>
      </div>

      <!-- Bottom notes -->
      <template #footer>
        <div class="flex gap-48 p-3">
          <div class="flex flex-col gap-2">
            <span class="text-text-secondary">Non-Dispatched Days</span>
            <div class="flex gap-2 items-center min-w-xs">
              <span :class="['text-3xl  font-medium', { 'text-emerald-400': nonDispatchDaysInRange !== '-' }]">{{
                nonDispatchDaysInRange
              }}</span>
              <span v-if="selectedDate" class="text-text-tertiary"
                >From {{ rangeStartDate?.toFormat('LLL dd, yyyy') }} to
                {{ rangeEndDate?.toFormat('LLL dd, yyyy') }}</span
              >
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <span class="text-text-secondary">Days Left Until Next Estimated Reset</span>
            <div class="flex gap-2 items-center">
              <span class="text-3xl text-text-secondary font-medium">{{ daysToNextReset }}</span>
              <span class="text-text-tertiary">till {{ nextResetProjection?.date?.toFormat('LLL dd, yyyy') }}</span>
            </div>
            <span v-if="observedNextResetProjections.length > 1" class="text-text-tertiary text-sm">
              Also projected:
              {{
                observedNextResetProjections
                  .slice(1)
                  .map((p) => p.date.toFormat('LLL dd'))
                  .join(', ')
              }}
            </span>
          </div>
        </div>
      </template>
    </el-card>
  </ViewLayout>
</template>

<style scoped lang="scss">
@use '@/styles/element/overrides';

:deep() {
  @include overrides.custom-el-tabs;
  @include overrides.wide-el-tabs;

  .el-card {
    &__header {
      display: flex;
      align-items: center;
      flex-direction: column;
      gap: 12px;
    }

    &__body {
      width: 100%;
    }
  }

  .calendar-date {
    &.el-card {
      display: flex;
    }

    .el-card__body {
      padding: 12px;
    }
  }
}
</style>
